import { FunctionComponent, PropsWithChildren, createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { logAnalyticsEvent } from '@vestico/utils';

export const LABEL_TEXT_FONT_NAME = 'Inter';
const DISPLAY_TEXT_FONT_SIZE = '16px';
const DISPLAY_TEXT_FONT_WEIGHT = '600';

/**
 *
 * @param text: The text or texts to calculate.
 * @param fontFamily
 * @param maxWidth
 * @returns Dimensions (width and height) of the largest bounding box
 */
export const calculateLargestDisplayTextDimensions = (text: string | string[], fontFamily: string, maxWidth?: number) => {
  const textElement = document.createElement('span');

  textElement.style.fontFamily = fontFamily;
  textElement.style.fontSize = DISPLAY_TEXT_FONT_SIZE;
  textElement.style.fontWeight = DISPLAY_TEXT_FONT_WEIGHT;
  textElement.style.height = 'auto';
  textElement.style.width = 'auto';
  textElement.style.position = 'absolute';
  textElement.style.visibility = 'hidden';
  textElement.style.whiteSpace = 'no-wrap';

  if (maxWidth) {
    textElement.style.maxWidth = `${maxWidth}px`;
  }

  const textArray = typeof text === 'string' ? [text] : text;

  document.body.appendChild(textElement);
  let largestHeight = 0;
  let largestWidth = 0;

  for (const textValue of textArray) {
    textElement.innerHTML = textValue;
    const currentWidth = textElement.clientWidth;
    const currentHeight = textElement.clientHeight;

    if (largestHeight < currentHeight) {
      largestHeight = currentHeight;
    }
    if (largestWidth < currentWidth) {
      largestWidth = currentWidth;
    }
  }

  document.body.removeChild(textElement);

  return { width: largestWidth, height: largestHeight };
};

export interface CustomFontContextProps {
  labelFontLoaded: boolean;
}
export const CustomFontContext = createContext<CustomFontContextProps>({ labelFontLoaded: false });

export const CustomFontContextProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const [labelFontLoaded, setLabelFontLoaded] = useState(false);

  const updateLabelFontLoadedIfNeeded = useCallback(() => {
    if (labelFontLoaded) {
      return;
    }

    try {
      const didLoadFont = document.fonts.check(`${DISPLAY_TEXT_FONT_WEIGHT} ${DISPLAY_TEXT_FONT_SIZE} ${LABEL_TEXT_FONT_NAME}`);

      if (didLoadFont) {
        setLabelFontLoaded(true);
      }
    } catch (error) {
      console.error(error);
    }
  }, [labelFontLoaded]);

  useEffect(() => {
    document.fonts.onloadingdone = updateLabelFontLoadedIfNeeded;
    document.fonts.ready.then(updateLabelFontLoadedIfNeeded).catch((e) => {
      console.error(e);
      logAnalyticsEvent({ category: 'exception', event: 'fonts-ready', name: e.message });
    });

    return () => {
      document.fonts.onloadingdone = null;
    };
  }, [updateLabelFontLoadedIfNeeded]);

  const contextValue: CustomFontContextProps = useMemo(() => {
    return { labelFontLoaded };
  }, [labelFontLoaded]);

  return <CustomFontContext.Provider value={contextValue}>{children}</CustomFontContext.Provider>;
};
